import { useEffect, useState } from "react";
import { Package, Save, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Iter325 — Owner-only bespoke package editor modal.
 * All override fields optional. Missing fields = "use standard".
 */
export default function BespokeEditorModal({ festival, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !festival) return;
    (async () => {
      try {
        const r = await api.get(`/festival/${festival.id}/bespoke`);
        const bp = r.data?.bespoke || null;
        setExisting(bp);
        setForm(bp ? {
          tier_amount: bp.tier_amount ?? "",
          player_cap: bp.player_cap ?? "",
          check_in_helpers: bp.check_in_helpers ?? "",
          court_ops_helpers: bp.court_ops_helpers ?? "",
          deputies: bp.deputies ?? "",
          self_service_amount: bp.self_service_amount ?? "",
          early_access_hours: bp.early_access_hours ?? "",
          early_access_free: !!bp.early_access_free,
          early_access_amount: bp.early_access_amount ?? "",
          gifted_helpers: bp.gifted_helpers ?? "",
          note: bp.note ?? "",
        } : {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load bespoke");
      }
    })();
  }, [open, festival]);

  const numOr = (v) => (v === "" || v == null ? undefined : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        tier_amount: numOr(form.tier_amount),
        player_cap: numOr(form.player_cap),
        check_in_helpers: numOr(form.check_in_helpers),
        court_ops_helpers: numOr(form.court_ops_helpers),
        deputies: numOr(form.deputies),
        self_service_amount: numOr(form.self_service_amount),
        early_access_hours: numOr(form.early_access_hours),
        early_access_free: !!form.early_access_free,
        early_access_amount: numOr(form.early_access_amount),
        gifted_helpers: numOr(form.gifted_helpers),
        note: form.note || undefined,
      };
      const r = await api.post(`/festival/${festival.id}/bespoke`, body);
      toast.success(`Saved · ${r.data.bespoke?.name || "Bespoke package"}`);
      onSaved?.(r.data.bespoke);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this bespoke package? Standard pricing will apply.")) return;
    setBusy(true);
    try {
      await api.delete(`/festival/${festival.id}/bespoke`);
      toast.success("Bespoke removed");
      onSaved?.(null);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  if (!open || !festival) return null;
  const Row = ({ label, hint, children }) => (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-[#525252] mt-0.5">{hint}</div>}
    </label>
  );
  const NumInput = ({ k, step = 1, min = 0 }) => (
    <input
      type="number" step={step} min={min}
      value={form[k] ?? ""}
      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      data-testid={`bespoke-${k}`}
      className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white text-sm mt-1 focus:border-[#66C2FF] focus:outline-none"
      placeholder="—"
    />
  );

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      data-testid="admin-bespoke-modal"
    >
      <div
        className="w-full max-w-2xl bg-[#0A0A0A] border-2 border-[#66C2FF] p-5 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="label-mono text-[#66C2FF] text-[10px]">
              BESPOKE PACKAGE · {existing?.name || "NEW"}
            </div>
            <h2 className="h-display text-xl mt-1 flex items-center gap-2">
              <Package className="w-5 h-5 text-[#FFB020]" /> {festival.name}
            </h2>
            <p className="text-xs text-[#A3A3A3] mt-1">
              Leave any field blank to use the standard price. Overrides apply to this festival only.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-[#A3A3A3] hover:text-white" data-testid="admin-bespoke-close" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Row label="Tier amount (£)"><NumInput k="tier_amount" step={0.01} /></Row>
          <Row label="Player cap"><NumInput k="player_cap" min={1} /></Row>
          <Row label="Check-in helpers"><NumInput k="check_in_helpers" /></Row>
          <Row label="Court OPS helpers"><NumInput k="court_ops_helpers" /></Row>
          <Row label="Deputies"><NumInput k="deputies" /></Row>
          <Row label="Self-service amount (£)"><NumInput k="self_service_amount" step={0.01} /></Row>
          <Row label="Early-access hours" hint="Bespoke > global default (25h)."><NumInput k="early_access_hours" /></Row>
          <Row label="Early-access amount (£)" hint="Ignored if 'Free' is ticked."><NumInput k="early_access_amount" step={0.01} /></Row>
          <Row label="Gifted helpers" hint="Free helper accounts for loyalty / sponsorship."><NumInput k="gifted_helpers" /></Row>
          <label className="flex items-center gap-2 self-end mb-1">
            <input
              type="checkbox"
              checked={!!form.early_access_free}
              onChange={(e) => setForm({ ...form, early_access_free: e.target.checked })}
              data-testid="bespoke-early_access_free"
              className="accent-[#CCFF00] w-4 h-4"
            />
            <span className="text-xs text-white">Early access free</span>
          </label>
        </div>

        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Note (visible to owner only)</div>
          <textarea
            value={form.note ?? ""}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            data-testid="bespoke-note"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white text-sm mt-1 focus:border-[#66C2FF] focus:outline-none min-h-[80px]"
            placeholder="Loyalty discount, sponsorship deal, etc."
            maxLength={800}
          />
        </div>

        <div className="flex items-center justify-between gap-2 mt-4">
          {existing ? (
            <button
              type="button" onClick={remove} disabled={busy}
              data-testid="admin-bespoke-delete"
              className="inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-white disabled:opacity-40"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete package
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button
              type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button" onClick={save} disabled={busy}
              data-testid="admin-bespoke-save"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#66C2FF] text-black hover:bg-white disabled:opacity-40"
            >
              <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save package"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
